import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Plane, UtensilsCrossed, Car, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import AccountShell from "@/components/vangard/AccountShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATALOG = [
  { id: "travel", icon: Plane, heading: "vc.svc.c1-title", body: "vc.svc.c1-body", items: ["vc.svc.c1-i1", "vc.svc.c1-i2", "vc.svc.c1-i3", "vc.svc.c1-i4"] },
  { id: "dining", icon: UtensilsCrossed, heading: "vc.svc.c2-title", body: "vc.svc.c2-body", items: ["vc.svc.c2-i1", "vc.svc.c2-i2", "vc.svc.c2-i3", "vc.svc.c2-i4"] },
  { id: "transport", icon: Car, heading: "vc.svc.c3-title", body: "vc.svc.c3-body", items: ["vc.svc.c3-i1", "vc.svc.c3-i2", "vc.svc.c3-i3", "vc.svc.c3-i4"] },
  { id: "acquisition", icon: Gift, heading: "vc.svc.c4-title", body: "vc.svc.c4-body", items: ["vc.svc.c4-i1", "vc.svc.c4-i2", "vc.svc.c4-i3", "vc.svc.c4-i4"] },
];

export default function AccountServicesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [message, setMessage] = useState("");
  const [date, setDate] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.error(t("vc.acct.svc.need-detail")); return; }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: user?.name || "Member",
        email: user?.email,
        phone: user?.phone || null,
        service_type: open,
        message: message.trim(),
        preferred_date: date || null,
      }, { withCredentials: true });
      toast.success(t("vc.acct.svc.sent"));
      setOpen(null); setMessage(""); setDate("");
      navigate("/account/concierge");
    } catch {
      toast.error(t("vc.acct.svc.fail"));
    } finally {
      setSending(false);
    }
  };

  return (
    <AccountShell active="/account/services" title={t("vc.acct.nav.services")}>
      <div data-testid="account-services-page">
        <p className="text-sm text-neutral-400 font-light max-w-lg">{t("vc.acct.svc.sub")}</p>

        <div className="mt-6 space-y-3">
          {CATALOG.map((cat, i) => (
            <div key={cat.id} className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 overflow-hidden" data-testid={`service-cat-${cat.id}`}>
              <div className="p-5 flex flex-col lg:flex-row lg:items-center gap-5 justify-between">
                <div className="flex items-start gap-4 min-w-0">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-600 mt-1 w-6">0{i + 1}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <cat.icon size={15} strokeWidth={1.5} className="text-neutral-400" />
                      <h2 className="text-base">{t(cat.heading)}</h2>
                    </div>
                    <p className="mt-1.5 text-[13px] text-neutral-400 font-light leading-relaxed max-w-[52ch]">{t(cat.body)}</p>
                    <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[11px] text-neutral-400 font-light">
                      {cat.items.map((it) => (
                        <li key={it} className="flex items-center gap-2">
                          <span className="h-px w-3 bg-white/35" />
                          {t(it)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(open === cat.id ? null : cat.id)}
                  data-testid={`service-request-${cat.id}`}
                  className="shrink-0 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                >
                  {t("vc.acct.svc.request")} <ArrowRight size={12} />
                </button>
              </div>

              {open === cat.id && (
                <div className="border-t border-white/[0.06] p-5 space-y-4" data-testid={`service-form-${cat.id}`}>
                  <label className="block">
                    <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.acct.svc.detail")}</span>
                    <textarea
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("vc.acct.svc.detail-ph")}
                      data-testid="service-message-input"
                      className="mt-2 w-full bg-transparent border border-white/[0.12] rounded-xl focus:border-white/45 outline-none py-2.5 px-3.5 text-[13px] font-light transition-colors placeholder:text-neutral-600"
                    />
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
                    <label className="block">
                      <span className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.acct.req.preferred")}</span>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="service-date-input"
                        className="mt-2 block bg-transparent border-0 border-b border-white/15 focus:border-white/60 outline-none py-1.5 text-[13px] font-light"
                      />
                    </label>
                    <button
                      onClick={send}
                      disabled={sending}
                      data-testid="service-send-btn"
                      className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-40 transition-colors"
                    >
                      {sending ? t("vc.acct.svc.sending") : t("vc.acct.svc.send")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AccountShell>
  );
}
